import color from "@constants/color";
import React from "react";
import { View, StyleSheet, TouchableOpacity } from "react-native";
import Text from "@components/CustomText";
import ChevronRight from "@assets/svg/ChevronRight";
import Tag from "./Tag";
import ButtonError from "@assets/svg/DbManagement/Error";
import { ReturnItem } from "../utils";
import FastImage from "@d11/react-native-fast-image";

interface OrderItemCardProps {
  item: ReturnItem;
  onPress?: () => void;
}

const parseTextWithBold = (
  text: string,
): { prefix: string; boldText: string } => {
  if (!text) return { prefix: "", boldText: "" };
  const parts = text.split(/<b>(.*?)<\/b>/);
  if (parts.length >= 2) {
    return {
      prefix: parts[0],
      boldText: parts[1] || "",
    };
  }
  return { prefix: text, boldText: "" };
};

const OrderItemCard: React.FC<OrderItemCardProps> = ({ item, onPress }) => {
  return (
    <TouchableOpacity onPress={onPress} activeOpacity={0.8}>
      <View style={styles.card}>
        <View style={styles.mainContent}>
          <View style={styles.imageContainer}>
            <FastImage
              source={{ uri: item.product_image }}
              style={styles.image}
            />
            <View style={styles.quantityCirle}>
              <Text variant="caption_2" style={styles.quantityText}>
                {item.qty}
              </Text>
            </View>
          </View>
          <View style={styles.details}>
            <Text variant="caption_4" style={styles.title}>
              {item.short_name}
            </Text>
            <Text variant="caption_1" style={styles.price}>
              {item.amount_txt}
            </Text>
            {item.return_reason && (
              <Tag
                text={item.return_reason_txt}
                bgColor="rgba(255, 77, 79, 0.05)"
                textColor={color.plain_red}
              />
            )}
          </View>
          <View style={styles.chevronContainer}>
            <ChevronRight pathFill={color.black06} style={styles.chevronIcon} />
          </View>
        </View>
        {item.reason_text || item.date_text ? (
          <View style={styles.cancellationInfo}>
            {item.reason_text ? (
              <Text variant="caption_2" style={styles.cancellationText}>
              {parseTextWithBold(item.reason_text).prefix}
              <Text
                variant="caption_2"
                style={[
                  styles.cancellationText,
                  { color: color.mediumBlack },
                  styles.boldText,
                ]}
              >
                {parseTextWithBold(item.reason_text).boldText}
              </Text>
            </Text> ): null}
            {item.date_text ? (
              <Text variant="caption_2" style={styles.cancellationText}>
              {parseTextWithBold(item.date_text).prefix}
              <Text
                variant="caption_2"
                style={[
                  styles.cancellationText,
                  { color: color.mediumBlack },
                  styles.boldText,
                ]}
              >
                {parseTextWithBold(item.date_text).boldText}
              </Text>
            </Text> ): null}
          </View>
        ) : null}
      </View>
      {item.cl_cx_not_picked_by_db_count ? (
        <View style={styles.errorContainer}>
          <ButtonError />
          <Text variant="caption_4" style={styles.errorText}>
            {`${item.cl_cx_not_picked_by_db_count} times `}
            <Text variant="caption_2" style={{ color: color.mediumBlack }}>
              {"CityMall attempted pickup but you didn't returned"}
            </Text>
          </Text>
        </View>
      ) : null}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: color.white,
    borderRadius: 12,
    padding: 12,
    marginTop: 12,
  },
  mainContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  quantityText: {
    color: color.white,
    fontWeight: "bold",
  },
  image: {
    width: 50,
    height: 50,
  },
  details: {
    flex: 1,
  },
  title: {
    color: color.black06,
  },
  price: {
    color: color.mediumBlack,
    marginBottom: 4,
  },
  chevronContainer: {
    backgroundColor: color.lightGrayWhite,
    alignSelf: "flex-start",
    padding: 3,
    borderRadius: 4,
  },
  chevronIcon: {
    height: 16,
    width: 16,
  },
  cancellationInfo: {
    backgroundColor: color.primaryBg,
    marginTop: 12,
    borderRadius: 6,
    paddingHorizontal: 5,
    paddingVertical: 4,
    paddingTop: 8,
  },
  cancellationText: {
    color: color.mediumBlacklightBlack,
    marginBottom: 4,
  },
  imageContainer: {
    backgroundColor: color.white,
    borderRadius: 12,
    padding: 10,
    justifyContent: "center",
    marginRight: 12,
  },
  quantityCirle: {
    position: "absolute",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    backgroundColor: color.mediumBlack,
    top: -8,
    right: -4,
    padding: 2,
    paddingHorizontal: 6,
  },
  errorContainer: {
    backgroundColor: "rgba(255, 228, 228, 1)",
    flexDirection: "row",
    borderBottomEndRadius: 12,
    borderBottomStartRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 6,
    paddingTop: 16,
    zIndex: -1,
    top: -10,
    alignContent: "flex-start",
  },
  errorText: {
    marginLeft: 8,
    color: color.plain_red,
  },
  boldText: {
    fontWeight: "600",
    color: color.black,
  },
});

export default OrderItemCard;
